/**
 * The two card patterns in the design library (Figma xUvlkD6o2mgno6JQbMPi14).
 * Structure is shared — a question, a list of option rows, an "Other…" row that
 * becomes free text, one question per step — and the variants differ in theme
 * and in what answering means:
 *
 * - `input` (node 8667-35919): the run is blocked on this card, so answering
 *   moves the run forward.
 * - `feedback` (node 8764-26408): pushed to the user but never required.
 *
 * All states live at node 8695-22789.
 */
export type AskUserCardVariant = 'input' | 'feedback'

export interface AskUserCardTheme {
  /** The solid tint a row takes once it holds the answer. */
  accentSelected: string
  accentDot: string
  /**
   * The badge worn by every surface that announces this card. Lives beside the
   * accents so a card and the rows pointing at it can't end up different colours.
   */
  badgeSurface: string
  badgeGlyph: string
  /**
   * Focus signal for the free-text box. The blue card tracks its accent; the
   * brand card can't — yellow-on-light is too low-contrast to read as focus —
   * so it just firms up the border it already has.
   */
  otherFocusBorder: string
  /** Overrides on the `bold` Button variant for the primary CTA. */
  primaryButton: string
  submitLabel: string
  /** Whether the multi-step card draws the step progress bar. */
  showProgress: boolean
  /** Whether rows animate in with a staggered entrance. */
  staggerRows: boolean
}

// Tailwind cannot generate these accent classes from interpolated names.
export const ASK_USER_CARD_THEMES: Record<AskUserCardVariant, AskUserCardTheme> = {
  input: {
    accentSelected: 'bg-brand/10',
    accentDot: 'bg-brand',
    // The brand yellow is unreadable as a 12px glyph, so the badge deepens it to
    // amber over a pale tint, landing at the blue badge's contrast.
    badgeSurface: 'bg-brand-subtle',
    badgeGlyph: 'text-brand-subtle',
    otherFocusBorder: 'focus-within:border-hover',
    primaryButton: 'border-border bg-brand text-on-brand hover:bg-brand-hover',
    submitLabel: 'Submit',
    showProgress: true,
    staggerRows: false,
  },
  feedback: {
    accentSelected: 'bg-info/10',
    accentDot: 'bg-info',
    badgeSurface: 'bg-info-subtle',
    badgeGlyph: 'text-info-subtle',
    otherFocusBorder: 'focus-within:border-[color:var(--focus-ring-color)]',
    primaryButton: 'border-border bg-info text-on-info hover:bg-info-hover',
    submitLabel: 'Save preferences',
    // Matches `input` — the two variants differ only in what answering means,
    // so a feedback card looks, steps, and progresses exactly like a question
    // card.
    showProgress: true,
    staggerRows: false,
  },
}
